import { useState, useEffect } from 'react';
import Icon from '../UI/Icon';
import './TeamCarousel.css';

// Unique constant name for default avatar
const TEAM_CAROUSEL_DEFAULT_AVATAR = '/images/team/placeholder.jpg';

// Unique interface name
interface TeamCarouselMember {
  id: number;
  name: string;
  role: string;
  image: string;
}

const TeamCarousel = () => {
  const [currentCarouselIndex, setCurrentCarouselIndex] = useState(0);
  const [isCarouselAutoPlaying, setIsCarouselAutoPlaying] = useState(true);
  const [carouselImageErrors, setCarouselImageErrors] = useState<Record<number, boolean>>({});
  
  // Unique function name for getting image source
  const getCarouselImageSource = (member: TeamCarouselMember) => {
    if (carouselImageErrors[member.id]) {
      return TEAM_CAROUSEL_DEFAULT_AVATAR;
    }
    return member.image;
  };

  const carouselTeamMembers: TeamCarouselMember[] = [
    {
      id: 1,
      name: 'Sweetness Nomfusi Vos',
      role: 'Chairperson',
      image: '/team/sweetness.jpg'
    },
    {
      id: 2,
      name: 'Liah Faku',
      role: 'Deputy Chairperson',
      image: '/team/liah.jpg'
    },
    {
      id: 3,
      name: 'Luzuko Sizila',
      role: 'Secretary',
      image: '/team/luzuko.jpg'
    },
    {
      id: 4,
      name: 'Mahlubi Nkonzo',
      role: 'Board Member',
      image: '/team/mahlubi.jpg'
    },
    {
      id: 5,
      name: 'Onezwa Siko',
      role: 'Treasurer',
      image: '/team/onezwa.jpg'
    },
    {
      id: 6,
      name: 'Busisiwe Ndlhlovu',
      role: 'Board Member',
      image: '/team/busisiwe.jpg'
    }
  ];

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>, id: number) => {
    // Mark this image as errored in our state
    setCarouselImageErrors(prev => ({ ...prev, [id]: true }));
    
    // If the error is not already for the default avatar, try to load it
    if (e.currentTarget.src !== TEAM_CAROUSEL_DEFAULT_AVATAR) {
      e.currentTarget.src = TEAM_CAROUSEL_DEFAULT_AVATAR;
    }
  };

  const nextCarouselSlide = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    setCurrentCarouselIndex((prevIndex) => (prevIndex + 1) % carouselTeamMembers.length);  
  };

  const prevCarouselSlide = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    setCurrentCarouselIndex((prevIndex) => (prevIndex - 1 + carouselTeamMembers.length) % carouselTeamMembers.length);
  };

  useEffect(() => {
    let carouselInterval: NodeJS.Timeout;
    
    if (isCarouselAutoPlaying) {
      carouselInterval = setInterval(() => {
        setCurrentCarouselIndex(prev => (prev + 1) % carouselTeamMembers.length);
      }, 5000);
    }
    
    return () => clearInterval(carouselInterval);
  }, [isCarouselAutoPlaying, carouselTeamMembers.length]);

  const getSlideClass = (index: number) => {
    const total = carouselTeamMembers.length;
    const prevIndex = (currentCarouselIndex - 1 + total) % total;
    const nextIndex = (currentCarouselIndex + 1) % total;
    
    if (index === currentCarouselIndex) return 'active';
    if (index === prevIndex) return 'prev';
    if (index === nextIndex) return 'next';
    return '';
  };

  const visibleCarouselMembers = carouselTeamMembers.map((member, index) => ({
    ...member,
    displayImage: getCarouselImageSource(member),
    className: getSlideClass(index)
  }));

  return (
    <div className="team-carousel-component">
      <div className="carousel-container">
        <button 
          className="carousel-nav-button carousel-prev" 
          onClick={(e) => prevCarouselSlide(e)}
          onMouseEnter={() => setIsCarouselAutoPlaying(false)}
          onMouseLeave={() => setIsCarouselAutoPlaying(true)}
          aria-label="Previous team member"
          type="button"
        >
          <Icon name="FaChevronLeft" />
        </button>
        
        <div className="carousel-track">
          {visibleCarouselMembers.map((member) => (
            <div 
              key={`carousel-${member.id}`}
              className={`carousel-slide ${member.className}`}
            >
              <div className="carousel-member-card">
                <div className="carousel-member-image-container">
                  <img 
                    src={member.displayImage}
                    alt={member.name}
                    className="carousel-member-img"
                    onError={(e) => handleImageError(e, member.id)}
                    loading="lazy"
                  />
                </div>
                <div className="carousel-member-details">
                  <h3 className="carousel-member-name">{member.name}</h3>
                  <p className="carousel-member-role">{member.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <button 
          className="carousel-nav-button carousel-next" 
          onClick={(e) => nextCarouselSlide(e)}
          onMouseEnter={() => setIsCarouselAutoPlaying(false)}
          onMouseLeave={() => setIsCarouselAutoPlaying(true)}
          aria-label="Next team member"
          type="button"
        >
          <Icon name="FaChevronRight" />
        </button>
      </div>
      
      <div className="carousel-dots">
        {carouselTeamMembers.map((_, index) => (
          <button
            key={index}
            className={`carousel-dot ${currentCarouselIndex === index ? 'active' : ''}`}
            onClick={() => setCurrentCarouselIndex(index)}
            onMouseEnter={() => setIsCarouselAutoPlaying(false)}
            onMouseLeave={() => setIsCarouselAutoPlaying(true)}
            aria-label={`Go to slide ${index + 1}`}
            type="button"
          />
        ))}
      </div>
    </div>
  );
};

export default TeamCarousel;
